import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { emptyTableCellClassName } from '@/constants/ui'
import type { Profile, UserOrderStats } from '@/types/user'
import { listUserOrderStatsBulk } from '@/api/users'
import { getUserPhotoUrl } from '@/api/users'
import { listCompanies } from '@/api/companies'
import { LoadingSecondsBanner } from '@/components/ui/LoadingSecondsBanner'
import { Loader } from '@/components/ui/Loader'
import { prefetchUserDetail } from '@/utils/prefetch'
import { formatPhoneDisplay } from '@/utils/phone'

const roleKey: Record<string, string> = {
  admin: 'users.roles.admin',
  dispatcher: 'users.roles.dispatcher',
  manager: 'users.roles.manager',
  customer_supervisor: 'users.roles.customerSupervisor',
  contractor_supervisor: 'users.roles.contractorSupervisor',
  inspector: 'users.roles.inspector',
}

export function UserTable({
  users,
  loading,
}: {
  users: Profile[]
  loading?: boolean
}) {
  const { t } = useTranslation()
  const nav = useNavigate()
  const qc = useQueryClient()

  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'active'],
    queryFn: () => listCompanies({ pageSize: 200, type: 'all' }),
  })
  const companies = companiesData?.items ?? []
  function companyDisplayName(id: string | null) {
    if (!id) return '—'
    return companies.find((c) => c.id === id)?.name ?? id
  }

  // Один bulk-запрос статистики на все видимые строки. Ключ стабильный —
  // отсортированный JSON по ids, чтобы реактквери кэшировал страницу.
  const userIds = useMemo(() => users.map((u) => u.id).sort(), [users])
  const { data: statsMap } = useQuery<Record<string, UserOrderStats>>({
    queryKey: ['user-order-stats', 'bulk', userIds],
    queryFn: () => listUserOrderStatsBulk(userIds),
    enabled: userIds.length > 0,
  })
  const emptyStats: UserOrderStats = {
    total: 0,
    active: 0,
    completed: 0,
    cancelled: 0,
    refused: 0,
  }
  const getStats = (id: string): UserOrderStats => statsMap?.[id] ?? emptyStats
  const isInitialLoading = Boolean(loading) && users.length === 0
  const isBackgroundLoading = Boolean(loading) && users.length > 0
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
      {isBackgroundLoading ? <LoadingSecondsBanner /> : null}
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
          <tr>
            <th className="px-3 py-2">{t('users.fields.fullName')}</th>
            <th className="px-3 py-2">{t('users.fields.phone')}</th>
            <th className="px-3 py-2">{t('users.fields.company')}</th>
            <th className="px-3 py-2">{t('users.fields.role')}</th>
            <th className="px-3 py-2">{t('users.fields.pd')}</th>
            <th className="px-3 py-2">{t('users.table.orders')}</th>
            <th className="px-3 py-2">{t('users.table.conversion')}</th>
            <th className="px-3 py-2">{t('users.orders.status')}</th>
          </tr>
        </thead>
        <tbody>
          {isInitialLoading ? (
            <tr>
              <td colSpan={8} className={emptyTableCellClassName}>
                <Loader centered size="sm" />
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={8} className={emptyTableCellClassName}>
                {t('common.noData')}
              </td>
            </tr>
          ) : (
            users.map((u) => {
              const st = getStats(u.id)
              const userPhotoUrl = getUserPhotoUrl(u)
              const conv =
                st.total > 0
                  ? `${Math.round((st.completed / st.total) * 100)}%`
                  : '—'
              return (
                <tr
                  key={u.id}
                  className="cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                  onMouseEnter={() => {
                    qc.setQueryData(['profile', u.id], u)
                    prefetchUserDetail(qc, u.id)
                  }}
                  onFocus={() => {
                    qc.setQueryData(['profile', u.id], u)
                    prefetchUserDetail(qc, u.id)
                  }}
                  onClick={() => nav(`/users/${u.id}`)}
                >
                  <td className="px-3 py-2 font-medium text-[color:var(--stryzh-color-text-primary)]">
                    <div className="flex items-center gap-2">
                      {userPhotoUrl ? (
                        <img
                          src={userPhotoUrl}
                          alt={u.full_name}
                          className="h-8 w-8 rounded-full border border-[color:var(--stryzh-color-border-default)] object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] text-xs text-[color:var(--stryzh-color-text-secondary)]">
                          {u.full_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span>{u.full_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">{u.phone ? formatPhoneDisplay(u.phone) : t('common.noData')}</td>
                  <td className="px-3 py-2">{companyDisplayName(u.company_id)}</td>
                  <td className="px-3 py-2">{roleKey[u.role] ? t(roleKey[u.role]) : u.role}</td>
                  <td className="px-3 py-2">{u.has_pd_access ? '✓' : '—'}</td>
                  <td className="px-3 py-2">
                    {st.total} / {st.active} / {st.completed}
                  </td>
                  <td className="px-3 py-2">{conv}</td>
                  <td className="px-3 py-2">
                    {u.is_active ? (
                      <span className="inline-flex shrink-0 items-center rounded-[100px] border border-[rgba(247,147,26,0.2)] bg-[rgba(247,147,26,0.12)] px-2 py-0.5 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] font-medium uppercase tracking-[0.5px] text-[color:var(--stryzh-color-accent-orange)]">
                        {t('users.table.active')}
                      </span>
                    ) : (
                      <span className="inline-flex shrink-0 items-center rounded-[100px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.05)] px-2 py-0.5 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] font-medium uppercase tracking-[0.5px] text-[color:var(--stryzh-color-text-secondary)]">
                        {t('users.table.inactive')}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
